import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Class";
import ClearIcon from "@mui/icons-material/Clear";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DeleteIcon from "@mui/icons-material/Delete";
import NotesIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import StarIcon from "@mui/icons-material/Star";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useModals } from "../../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import countriesData from "../../../../data/countriesList.json";
import { formatUSPhone, invalidateContactsAndClients, menuPropsModal } from "../../../../utils";
import { ClientsTable } from "../../../clients/clientsTable";
import { useClients } from "../../../clients/hooks/useClients";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { DetailedWorkforceOverview } from "../../../employees/components/DetailedWorkforceOverview";
import { useWorkforceQuery } from "../../../workforce/hooks/useWorkforceQuery";
import { useOptionalVirtualAssistant } from "../../context/VirtualAssistantContext";
import { ContactType, IContact, IContactPhoneNumber } from "../../types/contacts.types";
import {
  performSmartContactCleanup,
  useCreateContact,
  useDeleteContact,
  useGetContacts,
  useLinkWorkforce,
  useUpdateContact,
} from "../hooks/useContacts";

const normalizePhoneDigits = (phone?: string | null) => String(phone ?? "").replace(/\D/g, "");

const phonesMatch = (phoneA?: string | null, phoneB?: string | null) => {
  const a = normalizePhoneDigits(phoneA);
  const b = normalizePhoneDigits(phoneB);
  if (!a || !b) return false;
  return a === b || a.endsWith(b) || b.endsWith(a);
};

const countries = countriesData;

const parsePhoneItem = (item: IContactPhoneNumber | string): IContactPhoneNumber & { countryCode: string; dialCode: string } => {
  const rawNumber = typeof item === "string" ? item : item.number || "";
  const label = typeof item === "string" ? "primary" : (item.label ?? "other");
  const isPrimary = typeof item === "string" ? true : !!item.isPrimary;
  const itemCountryCode = typeof item !== "string" ? (item as any).countryCode : undefined;
  const itemDialCode = typeof item !== "string" ? (item as any).dialCode : undefined;

  let countryCode = "US";
  let dialCode = "+1";
  let cleanNumber = rawNumber;

  if (itemCountryCode && itemDialCode) {
    countryCode = itemCountryCode;
    dialCode = itemDialCode;
  } else if (rawNumber) {
    const trimmed = rawNumber.trim();
    const sortedCountries = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length);

    if (trimmed.startsWith("+")) {
      if (trimmed.startsWith("+1")) {
        countryCode = "US";
        dialCode = "+1";
        cleanNumber = trimmed.slice(2).trim();
      } else {
        const matched = sortedCountries.find((c) => trimmed.startsWith(c.dialCode));
        if (matched) {
          countryCode = matched.code;
          dialCode = matched.dialCode;
          cleanNumber = trimmed.slice(matched.dialCode.length).trim();
        }
      }
    } else {
      const digits = trimmed.replace(/\D/g, "");
      if (digits.startsWith("1") && digits.length === 11) {
        countryCode = "US";
        dialCode = "+1";
        cleanNumber = digits.slice(1);
      } else {
        countryCode = "US";
        dialCode = "+1";
        cleanNumber = digits;
      }
    }
  }

  if (dialCode === "+1") {
    cleanNumber = formatUSPhone(cleanNumber);
  }

  return {
    countryCode,
    dialCode,
    number: cleanNumber,
    label,
    isPrimary,
  };
};

interface ContactDetailsProps {
  phoneNumber: string;
  companyId: number;
  contact?: IContact | null;
  onClose: () => void;
  allowEditPhone?: boolean;
  initialMode?: "create" | "link" | "both" | "link-client" | "link-workforce" | "link-prospect" | "link-applicant";
}

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    "&.Mui-focused fieldset": {
      borderColor: "#fc9700",
    },
    "&.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root": {
      color: "#fc9700",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#fc9700",
  },
};

const disabledInputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
  },
};

const LABEL_PRESETS = ["mobile", "work", "home", "secondary", "primary"];

export function ContactDetails({ phoneNumber, companyId, contact, onClose, allowEditPhone = false, initialMode = "both" }: ContactDetailsProps) {
  const { showSnackbar } = useGlobalSnackbar();
  const virtualAssistant = useOptionalVirtualAssistant();
  const selectedSmsContact = virtualAssistant?.selectedContact ?? null;
  const handleSelectContact = virtualAssistant?.handleSelectContact;
  const createContactMutation = useCreateContact(companyId);
  const updateContactMutation = useUpdateContact(companyId);
  const deleteContactMutation = useDeleteContact(companyId);
  const linkWorkforceMutation = useLinkWorkforce();
  const queryClient = useQueryClient();
  const { linkContact, loading: isClientsLoading } = useClients();

  const clearResolvedContactFromSmsCaches = (phones: string[]) => {
    queryClient.setQueriesData({ queryKey: ["sms", "contacts"] }, (oldData: any) => {
      if (!oldData?.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: (page.data ?? []).map((smsContact: any) => {
            const matches = phones.some((phone) => phonesMatch(smsContact.phoneNumber, phone));
            return matches ? { ...smsContact, contact: null } : smsContact;
          }),
        })),
      };
    });

    if (selectedSmsContact && handleSelectContact && phones.some((phone) => phonesMatch(selectedSmsContact.phoneNumber, phone))) {
      handleSelectContact({ ...selectedSmsContact, contact: null });
    }
  };

  const linkContactMutation = useMutation({
    mutationFn: async ({ client, contact }: { client: any; contact: any }) => {
      return await linkContact(client, contact);
    },
    onSuccess: async (res: any) => {
      const allPhones = phoneNumbers.map((p) => p.number).filter(Boolean);
      await performSmartContactCleanup(allPhones.length > 0 ? allPhones : [phoneNumber], companyId, queryClient, contact?.uid);
      await invalidateContactsAndClients(queryClient);
      if (res?.message) {
        showSnackbar(res.message, "info");
      } else {
        showSnackbar("Phone number linked to client successfully", "success");
      }
      onClose();
    },
    onError: (error: any) => {
      showSnackbar(error.message || "Failed to link phone number", "error");
    },
  });

  // Form states
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [type, setType] = useState<ContactType>(ContactType.OTHER);
  const [uiCategory, setUiCategory] = useState<string>("OTHER");

  const [phoneNumbers, setPhoneNumbers] = useState<(IContactPhoneNumber & { countryCode?: string; dialCode?: string })[]>([]);
  const [isCodesOpen, setIsCodesOpen] = useState(false);

  const [searchStr, setSearchStr] = useState("");
  const debouncedSearchStr = useDebounce(searchStr, 300);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [linkLabel, setLinkLabel] = useState("other");
  const [linkIsPrimary, setLinkIsPrimary] = useState(false);

  const [selectedClientForLink, setSelectedClientForLink] = useState<any | null>(null);
  const [selectedWorkforceForLink, setSelectedWorkforceForLink] = useState<any | null>(null);

  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [convertMenuAnchorEl, setConvertMenuAnchorEl] = useState<null | HTMLElement>(null);
  const seeTriggerRef = useRef<HTMLButtonElement | null>(null);
  const seeWorkforceTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { closeByKey, modals } = useModals();
  const isClientOrWorkforce =
    type === ContactType.CLIENT || type === ContactType.PROSPECT || type === ContactType.WORKFORCE || type === ContactType.JOB_APPLICANT;
  const isLinkSelected =
    !!selectedContact ||
    ((type === ContactType.CLIENT || type === ContactType.PROSPECT) && !!selectedClientForLink) ||
    ((type === ContactType.WORKFORCE || type === ContactType.JOB_APPLICANT) && !!selectedWorkforceForLink);
  const isClientPickerOpen = modals.some((modal) => modal.key === "Dashboard-Clients-Business" && !modal.minimized);
  const isWorkforcePickerOpen = modals.some((modal) => modal.key === "Link-Workforce-Modal" && !modal.minimized);
  const shouldFetchExistingContacts =
    !contact &&
    initialMode !== "link-client" &&
    initialMode !== "link-prospect" &&
    initialMode !== "link-workforce" &&
    initialMode !== "link-applicant";

  const mapUiCategoryToContactType = (uiCat: string): ContactType => {
    switch (uiCat) {
      case "VENDOR":
        return ContactType.VENDOR;
      case "PROSPECT":
        return ContactType.PROSPECT;
      case "JOB_APPLICANT":
        return ContactType.JOB_APPLICANT;
      case "CLIENT":
        return ContactType.CLIENT;
      case "WORKFORCE":
        return ContactType.WORKFORCE;
      case "OTHER":
      default:
        return ContactType.OTHER;
    }
  };

  useEffect(() => {
    if (selectedClientForLink) {
      setName(selectedClientForLink.firstName || selectedClientForLink.clientCompanyName || "");
      setLastName(selectedClientForLink.lastName || "");
      setEmail(selectedClientForLink.email || "");
    }
  }, [selectedClientForLink]);

  useEffect(() => {
    if (selectedWorkforceForLink) {
      setName(selectedWorkforceForLink.firstName || "");
      setLastName(selectedWorkforceForLink.lastName || "");
      setEmail(selectedWorkforceForLink.email || "");
    }
  }, [selectedWorkforceForLink]);

  const handleLinkToWorkforce = (workforce: any) => {
    const cleanedPhoneNumbers = phoneNumbers
      .map((p) => {
        const rawDigits = (p.number || "").replace(/\D/g, "");
        if (!rawDigits) return null;

        const dial = p.dialCode || "+1";
        const dialDigits = dial.replace(/\D/g, "");

        let phoneDigits = rawDigits;
        if (dialDigits && rawDigits.startsWith(dialDigits) && rawDigits.length > dialDigits.length) {
          phoneDigits = rawDigits.slice(dialDigits.length);
        }

        return {
          number: `${dial}${phoneDigits}`,
          label: p.label?.trim() || null,
          isPrimary: !!p.isPrimary,
        };
      })
      .filter((p): p is { number: string; label: string | null; isPrimary: boolean } => p !== null && Boolean(p.number));

    if (cleanedPhoneNumbers.length === 0) {
      showSnackbar("At least one phone number is required to link", "error");
      return;
    }

    const phonesPayload = cleanedPhoneNumbers.map((p) => {
      const digits = p.number.replace(/\D/g, "");
      let dialCode = "+1";
      let phone = digits;
      if (digits.startsWith("1") && digits.length === 11) {
        phone = digits.substring(1);
      }
      return {
        countryCode: "US",
        dialCode,
        phone,
        phoneNumber: dialCode + phone,
        tag: p.label || "other",
        isDefault: false,
      };
    });

    const existingPhones = workforce.phoneNumbers || [];
    const combinedPhones = [...phonesPayload];

    existingPhones.forEach((existing: any) => {
      const alreadyIncluded = combinedPhones.some((newPhone) => newPhone.phone === existing.phone);
      if (!alreadyIncluded) {
        combinedPhones.push(existing);
      }
    });

    linkWorkforceMutation.mutate(
      {
        workforceUid: workforce.uid,
        phoneNumbers: combinedPhones,
      },
      {
        onSuccess: async () => {
          const allPhones = phoneNumbers.map((p) => p.number).filter(Boolean);
          console.log("all phones", allPhones);
          await performSmartContactCleanup(allPhones.length > 0 ? allPhones : [phoneNumber], companyId, queryClient, contact?.uid);
          showSnackbar("Phone number linked to workforce member successfully", "success");
          closeByKey("Link-Workforce-Modal");
          onClose();
        },
        onError: (err: any) => {
          const errMsg = err.response?.data?.message || err.message || "Failed to link to workforce";
          showSnackbar(errMsg, "error");
        },
      },
    );
  };

  const { data: contactsRes, isLoading: isLoadingSearch } = useGetContacts(companyId, 1, 30, debouncedSearchStr, {
    enabled: shouldFetchExistingContacts,
  });
  const existingContactsList: IContact[] = (Array.isArray(contactsRes) ? (contactsRes as unknown as IContact[]) : contactsRes?.data) || [];

  // Initialize fields
  useEffect(() => {
    if (contact) {
      setName(contact.name || "");
      setLastName(contact.lastName || "");
      setEmail(contact.email || "");
      setNotes(contact.notes || "");
      const finalType = contact.type || ContactType.OTHER;
      setType(finalType);
      setUiCategory(finalType === ContactType.JOB_APPLICANT ? "JOB_APPLICANT" : finalType);
      const parsed = (contact.phoneNumbers || []).map(parsePhoneItem);
      setPhoneNumbers(parsed.length > 0 ? parsed : [parsePhoneItem({ number: phoneNumber, label: "primary", isPrimary: true })]);
      setSelectedContact(null);
      setSelectedClientForLink(null);
      setSelectedWorkforceForLink(null);
    } else {
      setName("");
      setLastName("");
      setEmail("");
      setNotes("");
      const defaultType =
        initialMode === "link-client"
          ? ContactType.CLIENT
          : initialMode === "link-prospect"
            ? ContactType.PROSPECT
            : initialMode === "link-workforce"
              ? ContactType.WORKFORCE
              : initialMode === "link-applicant"
                ? ContactType.JOB_APPLICANT
                : ContactType.OTHER;
      setType(defaultType);
      setUiCategory(defaultType);
      setPhoneNumbers([parsePhoneItem({ number: phoneNumber, label: "primary", isPrimary: true })]);
      setSelectedContact(null);
      setLinkLabel("other");
      setLinkIsPrimary(false);
      setSelectedClientForLink(null);
      setSelectedWorkforceForLink(null);
    }
  }, [phoneNumber, contact, initialMode]);

  const handleDelete = () => {
    if (!contact) return;

    const phonesToClear = [phoneNumber, ...(contact.phoneNumbers?.map((p) => p.number).filter(Boolean) ?? [])];

    deleteContactMutation.mutate(contact.uid, {
      onSuccess: () => {
        clearResolvedContactFromSmsCaches(phonesToClear);
        showSnackbar("Contact deleted successfully", "success");
        onClose();
      },
      onError: (error: any) => {
        const errMsg = error?.response?.data?.message || error?.message || "Failed to delete contact";
        showSnackbar(errMsg, "error");
      },
    });
  };

  const handleAddPhoneNumber = () => {
    setPhoneNumbers([...phoneNumbers, { countryCode: "US", dialCode: "+1", number: "", label: "other", isPrimary: false }]);
  };

  const handleRemovePhoneNumber = (index: number) => {
    if (phoneNumbers.length <= 1) return;
    const itemToRemove = phoneNumbers[index];
    const newNumbers = phoneNumbers.filter((_, idx) => idx !== index);

    if (itemToRemove.isPrimary && newNumbers.length > 0) {
      newNumbers[0].isPrimary = true;
    }
    setPhoneNumbers(newNumbers);
  };

  const handlePhoneNumberChange = (index: number, field: keyof IContactPhoneNumber, value: string | boolean) => {
    const updated = [...phoneNumbers];
    if (field === "isPrimary") {
      updated.forEach((p, idx) => {
        p.isPrimary = idx === index;
      });
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value,
      } as any;
    }
    setPhoneNumbers(updated);
  };

  const handleCountryCodeChange = (index: number, newCombinedValue: string) => {
    const [code, dial] = newCombinedValue.split("-");
    const updated = [...phoneNumbers];
    const currentItem = updated[index];
    let currentNum = currentItem.number || "";
    if (dial === "+1") {
      currentNum = formatUSPhone(currentNum);
    }
    updated[index] = {
      ...currentItem,
      countryCode: code,
      dialCode: dial,
      number: currentNum,
    };
    setPhoneNumbers(updated);
  };

  const handlePhoneTextChange = (index: number, val: string) => {
    const updated = [...phoneNumbers];
    const currentItem = updated[index];
    const dial = currentItem?.dialCode || "+1";

    let formatted = val;
    if (dial === "+1" || currentItem?.countryCode === "US") {
      const digits = val.replace(/\D/g, "").slice(0, 10);
      formatted = formatUSPhone(digits);
    }

    updated[index] = {
      ...currentItem,
      number: formatted,
    };
    setPhoneNumbers(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Filter and clean phone numbers
    const cleanedPhoneNumbers = phoneNumbers
      .map((p) => {
        const rawDigits = (p.number || "").replace(/\D/g, "");
        if (!rawDigits) return null;

        const dial = p.dialCode || "+1";
        const dialDigits = dial.replace(/\D/g, "");

        let phoneDigits = rawDigits;
        if (dialDigits && rawDigits.startsWith(dialDigits) && rawDigits.length > dialDigits.length) {
          phoneDigits = rawDigits.slice(dialDigits.length);
        }

        return {
          number: `${dial}${phoneDigits}`,
          label: p.label?.trim() || null,
          isPrimary: !!p.isPrimary,
        };
      })
      .filter((p): p is { number: string; label: string | null; isPrimary: boolean } => p !== null && Boolean(p.number));

    if ((type === ContactType.CLIENT || type === ContactType.PROSPECT) && selectedClientForLink) {
      const contactPayload = {
        uid: contact?.uid || "",
        name,
        lastName,
        email,
        notes,
        phoneNumbers: cleanedPhoneNumbers,
      };

      linkContactMutation.mutate({ client: selectedClientForLink, contact: contactPayload });
      return;
    }

    if ((type === ContactType.WORKFORCE || type === ContactType.JOB_APPLICANT) && selectedWorkforceForLink) {
      handleLinkToWorkforce(selectedWorkforceForLink);
      return;
    }

    if (selectedContact) {
      // LINK (Attach number) to an existing contact
      createContactMutation.mutate(
        {
          companyId,
          contactUid: selectedContact.uid,
          phoneNumbers: [
            {
              number: phoneNumber.trim().replace(/[^\d+]/g, ""),
              label: linkLabel.trim() || null,
              isPrimary: (selectedContact.phoneNumbers?.length || 0) > 0 ? linkIsPrimary : true,
            },
          ],
          name: selectedContact.name,
          lastName: selectedContact.lastName,
          type: selectedContact.type,
          email: selectedContact.email,
          notes: selectedContact.notes,
        },
        {
          onSuccess: () => {
            showSnackbar("Phone number linked to contact successfully", "success");
            onClose();
          },
          onError: (error: any) => {
            const errMsg = error?.response?.data?.message || error?.message || "Failed to link number";
            showSnackbar(errMsg, "error");
          },
        },
      );
      return;
    }

    // CREATE OR UPDATE CONTACT
    if (!name.trim()) {
      showSnackbar("Name is required", "error");
      return;
    }

    if (cleanedPhoneNumbers.length === 0) {
      showSnackbar("At least one phone number is required", "error");
      return;
    }

    // Enforce at least one primary
    const hasPrimary = cleanedPhoneNumbers.some((p) => p.isPrimary);
    if (!hasPrimary && cleanedPhoneNumbers.length > 0) {
      cleanedPhoneNumbers[0].isPrimary = true;
    }

    if (contact) {
      // UPDATE Existing Contact
      updateContactMutation.mutate(
        {
          uid: contact.uid,
          payload: {
            uid: contact.uid,
            companyId,
            phoneNumbers: cleanedPhoneNumbers,
            name: name.trim(),
            lastName: lastName.trim() || null,
            email: email.trim() || null,
            notes: notes.trim() || null,
            type,
          },
        },
        {
          onSuccess: () => {
            showSnackbar("Contact updated successfully", "success");
            onClose();
          },
          onError: (error: any) => {
            const errMsg = error?.response?.data?.message || error?.message || "Failed to update contact";
            showSnackbar(errMsg, "error");
          },
        },
      );
    } else {
      // CREATE a brand new contact
      createContactMutation.mutate(
        {
          companyId,
          phoneNumbers: cleanedPhoneNumbers,
          name: name.trim(),
          lastName: lastName.trim() || null,
          email: email.trim() || null,
          notes: notes.trim() || null,
          type,
        },
        {
          onSuccess: () => {
            showSnackbar("Contact created successfully", "success");
            onClose();
          },
          onError: (error: any) => {
            const errMsg = error?.response?.data?.message || error?.message || "Failed to create contact";
            showSnackbar(errMsg, "error");
          },
        },
      );
    }
  };

  const isLoading =
    createContactMutation.isPending ||
    updateContactMutation.isPending ||
    deleteContactMutation.isPending ||
    linkWorkforceMutation.isPending ||
    linkContactMutation.isPending ||
    isClientsLoading;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header Info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: !contact && !allowEditPhone ? 0 : 2.5, px: 1 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: "rgba(252, 151, 0, 0.1)",
            color: "#fc9700",
          }}
        >
          {contact ? <ContactMailIcon sx={{ fontSize: 22 }} /> : <PersonIcon sx={{ fontSize: 22 }} />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          {phoneNumber && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 700, lineHeight: 1.2 }}>
                {formatUSPhone(phoneNumber)}
              </Typography>
              {!selectedContact &&
                phoneNumbers.length > 1 &&
                (() => {
                  const assocIndex = phoneNumbers.findIndex((p) => p.number === phoneNumber);
                  if (assocIndex === -1) return null;
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!phoneNumbers[assocIndex].isPrimary}
                          onChange={(e) => handlePhoneNumberChange(assocIndex, "isPrimary", e.target.checked)}
                          color="warning"
                          size="small"
                          sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                          Primary
                        </Typography>
                      }
                      sx={{ ml: 0 }}
                    />
                  );
                })()}
              {selectedContact && (selectedContact.phoneNumbers?.length || 0) > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={linkIsPrimary}
                      onChange={(e) => setLinkIsPrimary(e.target.checked)}
                      color="warning"
                      size="small"
                      sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                      Primary
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 1, mb: 3, mt: 1.5 }}>
          {/* Link to Existing Contact (Search Bar) - shown when creating or linking */}
          {!contact && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Autocomplete
                options={existingContactsList}
                value={selectedContact}
                getOptionLabel={(option) => {
                  const nameParts = [option.name, option.lastName].filter(Boolean);
                  return nameParts.length > 0 ? nameParts.join(" ") : "Unnamed Contact";
                }}
                loading={isLoadingSearch}
                onInputChange={(_, value) => setSearchStr(value)}
                onChange={(_, value) => setSelectedContact(value)}
                renderOption={(props, option) => {
                  const primaryNum = option.phoneNumbers?.find((p) => p.isPrimary)?.number || option.phoneNumbers?.[0]?.number;
                  return (
                    <Box
                      component="li"
                      {...props}
                      key={option.uid}
                      sx={{
                        display: "flex !important",
                        flexDirection: "column !important",
                        alignItems: "flex-start !important",
                        textAlign: "left !important",
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "left" }}>
                        {option.name} {option.lastName || ""}
                      </Typography>
                      {primaryNum && (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "left" }}>
                          {formatUSPhone(primaryNum)}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Existing Contact to Link"
                    placeholder="Search by name..."
                    sx={inputStyles}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isLoadingSearch ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Selected Contact Preview card */}
              {selectedContact && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(252, 151, 0, 0.04)",
                    border: "1px solid",
                    borderColor: "warning.light",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "warning.dark" }}>
                      Link to Selected Contact:
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setSelectedContact(null)}
                      startIcon={<ClearIcon fontSize="small" />}
                      sx={{ textTransform: "none", color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700 }}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Name: {selectedContact.name} {selectedContact.lastName || ""}
                  </Typography>
                  {selectedContact.email && (
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                      Email: {selectedContact.email}
                    </Typography>
                  )}

                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 1 }}>
                      Existing Numbers:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedContact.phoneNumbers?.map((p, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.2,
                            py: 0.5,
                            borderRadius: "8px",
                            backgroundColor: p.isPrimary ? "var(--fx-colors-warning-100, #FFEDD5)" : "var(--fx-surface-muted, #F1F5F9)",
                            border: "1px solid",
                            borderColor: p.isPrimary ? "#ffe0b2" : "var(--fx-surface-border, #E2E8F0)",
                          }}
                        >
                          {p.isPrimary && <StarIcon sx={{ fontSize: 13, color: "#fc9700" }} />}
                          <Typography variant="caption" sx={{ fontWeight: 600, color: p.isPrimary ? "#e08600" : "var(--fx-surface-textsecondary, #64748B)" }}>
                            {formatUSPhone(p.number)}
                          </Typography>
                          {p.label && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "var(--fx-surface-textsecondary, #64748B)",
                                ml: 0.5,
                                fontSize: "0.65rem",
                                textTransform: "lowercase",
                                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                                px: 0.5,
                                py: 0.1,
                                borderRadius: "4px",
                                border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
                              }}
                            >
                              {p.label}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {!isLinkSelected && (
                <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{ px: 1.5, color: "text.secondary", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 0.5 }}
                  >
                    Or Create New Contact
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>
              )}
            </Box>
          )}

          {/* Form inputs: rendered only when no link item is selected */}
          {!isLinkSelected && (
            <>
              {/* Name Details */}
              <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                  disabled={isLoading}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Last Name (optional)"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  disabled={isLoading}
                  sx={inputStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Email Detail */}
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={isLoading}
                sx={inputStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Contact Category Detail */}
              {type !== ContactType.CLIENT && type !== ContactType.WORKFORCE && (
                <FormControl fullWidth variant="outlined" disabled={isLoading} sx={inputStyles}>
                  <InputLabel id="contact-type-label">Contact Category</InputLabel>
                  <Select
                    labelId="contact-type-label"
                    value={uiCategory}
                    onChange={(e) => {
                      const val = e.target.value;
                      setUiCategory(val);
                      setType(mapUiCategoryToContactType(val));
                    }}
                    label="Contact Category"
                    startAdornment={
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <CategoryIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                      </InputAdornment>
                    }
                    sx={{
                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Conversion / Link Button Menu */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, mb: 0.5 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<LinkIcon sx={{ fontSize: 16, color: "#fc9700" }} />}
                  onClick={(e) => setConvertMenuAnchorEl(e.currentTarget)}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#fc9700",
                    textTransform: "none",
                    py: 0.5,
                    px: 1,
                    borderRadius: "6px",
                    "&:hover": {
                      bgcolor: "rgba(252, 151, 0, 0.08)",
                    },
                  }}
                >
                  Convert / Link to...
                </Button>

                <Menu
                  anchorEl={convertMenuAnchorEl}
                  open={Boolean(convertMenuAnchorEl)}
                  onClose={() => setConvertMenuAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      minWidth: 200,
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                      p: 0.5,
                    },
                  }}
                >
                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, color: "var(--fx-surface-texttertiary, #94A3B8)", textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}
                    >
                      Customer
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={() => {
                      setConvertMenuAnchorEl(null);
                      onClose();
                      closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                      if (virtualAssistant?.openContactDetails) {
                        virtualAssistant.openContactDetails(phoneNumber, null, "link-prospect");
                      }
                    }}
                    sx={{ py: 1, px: 2, borderRadius: "6px" }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
                    </ListItemIcon>
                    <ListItemText primary="Prospect" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setConvertMenuAnchorEl(null);
                      onClose();
                      closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                      if (virtualAssistant?.openContactDetails) {
                        virtualAssistant.openContactDetails(phoneNumber, null, "link-client");
                      }
                    }}
                    sx={{ py: 1, px: 2, borderRadius: "6px" }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
                    </ListItemIcon>
                    <ListItemText primary="Client" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, color: "var(--fx-surface-texttertiary, #94A3B8)", textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}
                    >
                      Workforce
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={() => {
                      setConvertMenuAnchorEl(null);
                      onClose();
                      closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                      if (virtualAssistant?.openContactDetails) {
                        virtualAssistant.openContactDetails(phoneNumber, null, "link-applicant");
                      }
                    }}
                    sx={{ py: 1, px: 2, borderRadius: "6px" }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
                    </ListItemIcon>
                    <ListItemText primary="Applicant" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setConvertMenuAnchorEl(null);
                      onClose();
                      closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                      if (virtualAssistant?.openContactDetails) {
                        virtualAssistant.openContactDetails(phoneNumber, null, "link-workforce");
                      }
                    }}
                    sx={{ py: 1, px: 2, borderRadius: "6px" }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
                    </ListItemIcon>
                    <ListItemText primary="Workforce Member" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, color: "var(--fx-surface-texttertiary, #94A3B8)", textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}
                    >
                      Vendor
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={() => {
                      setConvertMenuAnchorEl(null);
                      onClose();
                      closeByKey("Virtual-Receptionist-Contact-Details-Modal");
                      if (virtualAssistant?.openContactDetails) {
                        virtualAssistant.openContactDetails(phoneNumber, null, "link-vendor");
                      }
                    }}
                    sx={{ py: 1, px: 2, borderRadius: "6px" }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LinkIcon fontSize="small" sx={{ color: "#fc9700" }} />
                    </ListItemIcon>
                    <ListItemText primary="Vendor" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>
                </Menu>
              </Box>

              {/* Multiple Phone Numbers List */}
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                    Associated Phone Numbers
                  </Typography>

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddPhoneNumber}
                    disabled={isLoading}
                    sx={{
                      color: "#fc9700",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": {
                        bgcolor: "rgba(252, 151, 0, 0.08)",
                      },
                    }}
                  >
                    Add Number
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {phoneNumbers.map((phoneItem, index) => {
                    const isConversationNum = phonesMatch(phoneItem.number, phoneNumber) && !contact && !allowEditPhone;

                    if (isConversationNum) return null;

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                          p: 1.5,
                          borderRadius: "12px",
                          border: "1px solid var(--fx-surface-border, #E2E8F0)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "stretch",
                              flex: 1,
                            }}
                          >
                            {/* Country Code Select */}
                            <FormControl
                              sx={{
                                width: 105,
                                flexShrink: 0,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px 0 0 12px",
                                  height: "100%",
                                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                                  "& fieldset": {
                                    borderColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.23)",
                                    borderRightWidth: 0,
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#fc9700",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#fc9700",
                                    borderRightWidth: 0,
                                  },
                                },
                                "& .MuiSelect-select": {
                                  padding: "8.5px 22px 8.5px 10px !important",
                                  fontSize: "0.875rem",
                                  display: "flex",
                                  alignItems: "center",
                                },
                                "& .MuiSelect-icon": {
                                  right: "2px",
                                },
                              }}
                            >
                              <Select
                                MenuProps={menuPropsModal}
                                value={`${(phoneItem as any).countryCode || "US"}-${(phoneItem as any).dialCode || "+1"}`}
                                onChange={(e) => handleCountryCodeChange(index, e.target.value)}
                                onOpen={() => setIsCodesOpen(true)}
                                onClose={() => setIsCodesOpen(false)}
                                variant="outlined"
                                disabled={isLoading}
                                size="small"
                                renderValue={(selected) => {
                                  const [code, dialCode] = selected.split("-");
                                  let selectedCountry = countries.find((c) => c.code === code);
                                  if (!selectedCountry && dialCode === "+1") {
                                    selectedCountry = countries.find((c) => c.code === "US");
                                  }

                                  return selectedCountry ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                      <ReactCountryFlag
                                        countryCode={selectedCountry.code}
                                        svg
                                        style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }}
                                      />
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)", fontSize: "0.85rem" }}>
                                        {dialCode}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    ""
                                  );
                                }}
                              >
                                {countries.map((c) => (
                                  <MenuItem key={c.code} value={`${c.code}-${c.dialCode}`}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      <ReactCountryFlag
                                        countryCode={c.code}
                                        svg
                                        style={{ width: 20, height: 15, borderRadius: 2, objectFit: "cover" }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={isCodesOpen ? `${c.name} (${c.dialCode})` : c.dialCode}
                                      primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {/* Phone Number Input */}
                            <TextField
                              size="small"
                              label="Phone Number"
                              value={phoneItem.number}
                              onChange={(e) => handlePhoneTextChange(index, e.target.value)}
                              required
                              fullWidth
                              variant="outlined"
                              disabled={isLoading}
                              placeholder="(555) 000-0000"
                              sx={{
                                ...inputStyles,
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                  borderTopLeftRadius: 0,
                                  borderBottomLeftRadius: 0,
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "text.secondary",
                                      }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Box>

                          {phoneNumbers.length > 1 && (
                            <IconButton
                              color="error"
                              size="small"
                              disabled={isLoading}
                              onClick={() => handleRemovePhoneNumber(index)}
                              sx={{
                                flexShrink: 0,
                                width: 40,
                                height: 40,
                                border: "1px solid",
                                borderColor: "error.light",
                                borderRadius: "10px",
                                "&:hover": {
                                  bgcolor: "error.main",
                                  borderColor: "error.main",
                                  color: "#fff",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </Box>

                        {phoneNumbers.length > 1 && (
                          <FormControlLabel
                            sx={{
                              width: "fit-content",
                              ml: -0.5,
                              mr: 0,
                              mb: -0.5,
                            }}
                            control={
                              <Checkbox
                                checked={!!phoneItem.isPrimary}
                                onChange={(e) => handlePhoneNumberChange(index, "isPrimary", e.target.checked)}
                                color="warning"
                                size="small"
                                disabled={isLoading}
                              />
                            }
                            label={
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: "var(--fx-surface-textsecondary, #64748B)",
                                }}
                              >
                                Primary Contact Number
                              </Typography>
                            }
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Notes Detail */}
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                disabled={isLoading}
                sx={inputStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5, mr: 1 }}>
                      <NotesIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </Box>

        {/* Action Buttons */}
        {(!isClientOrWorkforce ||
          (type === ContactType.CLIENT && selectedClientForLink) ||
          (type === ContactType.WORKFORCE && selectedWorkforceForLink)) && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1, borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
            {contact && !isClientOrWorkforce && (
              <Button
                onClick={(e) => setDeleteAnchorEl(e.currentTarget)}
                disabled={isLoading}
                variant="text"
                sx={{
                  fontWeight: 800,
                  textTransform: "none",
                  color: "#ef4444",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  "&:hover": {
                    bgcolor: "rgba(239, 68, 68, 0.08)",
                  },
                }}
              >
                {deleteContactMutation.isPending ? <CircularProgress size={20} color="inherit" /> : "Delete Contact"}
              </Button>
            )}
            {contact && !isClientOrWorkforce && <Box sx={{ flex: 1 }} />}
            <Button
              onClick={() => {
                if (isClientOrWorkforce) {
                  setSelectedClientForLink(null);
                  setSelectedWorkforceForLink(null);
                } else {
                  onClose();
                }
              }}
              disabled={isLoading}
              variant="text"
              sx={{
                fontWeight: 800,
                textTransform: "none",
                color: "var(--fx-surface-textsecondary, #64748B)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              variant="contained"
              sx={{
                fontWeight: 800,
                textTransform: "none",
                minWidth: 110,
                borderRadius: "10px",
                boxShadow: "none",
                bgcolor: "#fc9700",
                color: "#fff",
                px: 2.5,
                py: 1,
                "&:hover": {
                  bgcolor: "#e08600",
                  boxShadow: "0 4px 12px rgba(252, 151, 0, 0.2)",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : type === ContactType.CLIENT && selectedClientForLink ? (
                "Link Client"
              ) : type === ContactType.WORKFORCE && selectedWorkforceForLink ? (
                "Link Workforce"
              ) : contact ? (
                "Update Contact"
              ) : selectedContact ? (
                "Link Contact"
              ) : (
                "Save Contact"
              )}
            </Button>
          </Box>
        )}
      </form>

      <BaseModalComponent
        modalLabel={type === ContactType.PROSPECT ? "My prospects" : "My clients"}
        modalKey="Dashboard-Clients-Business"
        onClose={() => {
          if (!selectedClientForLink && (initialMode === "link-client" || initialMode === "link-prospect")) {
            onClose();
          }
        }}
        elementTrigger={<button ref={seeTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        {isClientPickerOpen ? (
          <ClientsTable
            disableInnerChilds={true}
            disableRecordClickable
            initialActiveTable={type === ContactType.PROSPECT ? "prospects" : "clients"}
            contact={{
              uid: contact?.uid || "",
              name: name,
              lastName: lastName,
              email: email,
              notes: notes,
              phoneNumbers: phoneNumbers,
            }}
            onLinkContact={(client) => {
              setSelectedClientForLink(client);
              setSelectedWorkforceForLink(null);
              closeByKey("Dashboard-Clients-Business");
            }}
          />
        ) : null}
      </BaseModalComponent>

      <BaseModalComponent
        modalLabel={type === ContactType.JOB_APPLICANT ? "Link Applicant" : "Link Workforce Member"}
        modalKey="Link-Workforce-Modal"
        onClose={() => {
          if (!selectedWorkforceForLink && (initialMode === "link-workforce" || initialMode === "link-applicant")) {
            onClose();
          }
        }}
        defaultSizes={{ width: 920, height: 640 }}
        elementTrigger={<button ref={seeWorkforceTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
      >
        {isWorkforcePickerOpen ? (
          <DetailedWorkforceOverview
            contact={{
              uid: contact?.uid || "",
              name: name,
              lastName: lastName,
              email: email,
              notes: notes,
              phoneNumbers: phoneNumbers,
              type: type,
            }}
            initialFilter={type === ContactType.JOB_APPLICANT ? "applicant" : "all"}
            hideFilterToggle={type === ContactType.JOB_APPLICANT}
            onLinkContact={(member) => {
              setSelectedWorkforceForLink(member);
              setSelectedClientForLink(null);
              closeByKey("Link-Workforce-Modal");
            }}
          />
        ) : null}
      </BaseModalComponent>

      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => setDeleteAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 250,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.5 }}>Delete contact?</Typography>
        <Typography sx={{ fontSize: 11, color: "var(--fx-surface-textsecondary, #64748B)", mb: 2 }}>
          Are you sure you want to delete this contact? The conversations will be kept.
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            size="small"
            onClick={() => setDeleteAnchorEl(null)}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.5,
              px: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              handleDelete();
              setDeleteAnchorEl(null);
            }}
            sx={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 1.5,
              boxShadow: "none",
              py: 0.5,
              px: 1.5,
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

function WorkforceSelectionList({ onLink, isLoading }: { onLink: (workforce: any) => void; isLoading: boolean }) {
  const { data: workforceMembers = [], isLoading: isLoadingQuery } = useWorkforceQuery();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return workforceMembers;
    return (workforceMembers || []).filter((m: any) => {
      const fullName = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
      const email = (m.email || "").toLowerCase();
      return fullName.includes(q) || email.includes(q);
    });
  }, [workforceMembers, search]);

  if (isLoadingQuery) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField size="small" placeholder="Search workforce members..." value={search} onChange={(e) => setSearch(e.target.value)} fullWidth />
      <Box sx={{ maxHeight: 400, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
            No workforce members found.
          </Typography>
        ) : (
          filtered.map((member: any) => {
            const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "Unnamed Member";
            return (
              <Box
                key={member.uid}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {member.email || "No email"} • {member.workforceRole || member.role || "No role"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={isLoading}
                  onClick={() => onLink(member)}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Link
                </Button>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
